import type {
  ContactValue,
  JsonSchemaType,
  Object as PortalObject,
  PositionsValue,
  StatusOption,
  StatusValue,
} from '@gmn-portal/backend';
import { DateTime } from 'luxon';
import { useCustomer, useObjectCategory } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';

/**
 * Format a contact value for display in the activity log.
 * Returns a string representation of the contact.
 */
function formatContactValue(contact: ContactValue | undefined | null): string {
  if (!contact) return '';

  const parts: string[] = [];

  if (contact.name) {
    parts.push(contact.name);
  }

  if (contact.email) {
    parts.push(contact.email);
  }

  if (contact.phone1) {
    parts.push(contact.phone1);
  }

  if (contact.phone2) {
    parts.push(contact.phone2);
  }

  if (contact.fallbackContact?.name) {
    parts.push(`[Fallback: ${contact.fallbackContact.name}]`);
  }

  return parts.join(', ');
}

/**
 * Format a positions value for display in the activity log.
 * Returns a summary string of the positions.
 */
function formatPositionsValue(
  positions: PositionsValue | undefined | null,
  positionsLabel: string
): string {
  if (!positions || !Array.isArray(positions) || positions.length === 0) return '';

  const count = positions.length;
  const labels = positions
    .map((p) => p.label)
    .filter(Boolean)
    .slice(0, 3);

  if (labels.length === 0) {
    return `${count} ${positionsLabel}`;
  }

  const preview = labels.join(', ');
  if (count > 3) {
    return `${preview}, ... (${count})`;
  }

  return preview;
}

/**
 * Get the label for a status color from the schema options.
 */
function getStatusLabel(
  color: string | undefined,
  statusOptions: StatusOption[] | undefined,
  locale: string
): string {
  if (!color) return '';
  if (!statusOptions) return color;

  const option = statusOptions.find((opt) => opt.color === color);
  if (!option) return color;

  // Return localized label or German fallback
  return (locale === 'en-US' ? option.labelEn : option.labelDe) || option.labelDe;
}

/**
 * Format a status value for display in the activity log.
 * Returns a human-readable string describing the status.
 */
function formatStatusValue(
  status: StatusValue | undefined | null,
  statusOptions: StatusOption[] | undefined,
  locale: string,
  translations: { manual: string; scheduled: string; ranges: string; defaultStatus: string }
): string {
  if (!status || typeof status !== 'object') return '';

  const parts: string[] = [];

  if (status.mode === 'manual') {
    const statusLabel = getStatusLabel(status.manualStatus, statusOptions, locale);
    parts.push(`${translations.manual}: ${statusLabel}`);
  } else if (status.mode === 'scheduled') {
    const rangeCount = status.ranges?.length || 0;
    if (rangeCount > 0) {
      parts.push(`${translations.scheduled}: ${rangeCount} ${translations.ranges}`);
    }
    const defaultLabel = getStatusLabel(status.defaultStatus, statusOptions, locale);
    parts.push(`${translations.defaultStatus}: ${defaultLabel}`);
  }

  return parts.join(', ');
}

export interface ResolvedFieldValueProps {
  field: string;
  value: unknown;
  objectSchema?: JsonSchemaType;
  categorySchema?: JsonSchemaType;
  category?: PortalObject['category'];
  assignedCustomer?: PortalObject['assignedCustomer'];
  assignedHelicopterServiceProvider?: PortalObject['assignedHelicopterServiceProvider'];
}

export function ResolvedFieldValue({
  field,
  value,
  objectSchema,
  categorySchema,
  category,
  assignedCustomer,
  assignedHelicopterServiceProvider,
}: ResolvedFieldValueProps) {
  const { t, locale } = useIntl();

  const isRelationWithId = (
    input: unknown
  ): input is { id: string; name?: string; metadataSchema?: JsonSchemaType } => {
    return typeof input === 'object' && input !== null && 'id' in input;
  };
  // Always call hooks unconditionally to preserve the hook sequence.
  const effectiveAssignedCustomer = field === 'assignedCustomer' ? value : assignedCustomer;
  const assignedCustomerId =
    effectiveAssignedCustomer && isRelationWithId(effectiveAssignedCustomer)
      ? (effectiveAssignedCustomer.id ?? '')
      : typeof effectiveAssignedCustomer === 'string'
        ? effectiveAssignedCustomer
        : '';
  const { data: customer } = useCustomer(field === 'assignedCustomer' ? assignedCustomerId : '');
  const resolvedCustomerName =
    (effectiveAssignedCustomer && isRelationWithId(effectiveAssignedCustomer)
      ? effectiveAssignedCustomer.name
      : null) ?? customer?.name;

  const effectiveHeliProvider =
    field === 'assignedHelicopterServiceProvider' ? value : assignedHelicopterServiceProvider;
  const assignedHeliProviderId =
    effectiveHeliProvider && isRelationWithId(effectiveHeliProvider)
      ? (effectiveHeliProvider.id ?? '')
      : typeof effectiveHeliProvider === 'string'
        ? effectiveHeliProvider
        : '';
  const { data: heliProvider } = useCustomer(
    field === 'assignedHelicopterServiceProvider' ? assignedHeliProviderId : ''
  );
  const resolvedHeliProviderName =
    (effectiveHeliProvider && isRelationWithId(effectiveHeliProvider)
      ? effectiveHeliProvider.name
      : null) ?? heliProvider?.name;

  const effectiveCategory = field === 'category' ? value : category;
  const categoryId =
    effectiveCategory && isRelationWithId(effectiveCategory)
      ? (effectiveCategory.id ?? '')
      : ((effectiveCategory as string | undefined) ?? '');
  const { data: categoryData } = useObjectCategory(categoryId, {
    disabled: field !== 'category' || !categoryId || isRelationWithId(effectiveCategory),
  });
  const resolvedCategoryName =
    (effectiveCategory && isRelationWithId(effectiveCategory) ? effectiveCategory.name : null) ??
    categoryData?.name;
  const resolvedCategorySchema =
    categorySchema ||
    (effectiveCategory && isRelationWithId(effectiveCategory)
      ? (effectiveCategory.metadataSchema as JsonSchemaType | undefined)
      : (categoryData?.metadataSchema as JsonSchemaType | undefined));

  if (value === null || value === undefined || value === '') {
    return <span className="font-medium">{t('activity.none')}</span>;
  }

  const isMetadataField =
    field.startsWith('dynamicMetadata.') || field.startsWith('categoryMetadata.');

  if (
    !isMetadataField &&
    field !== 'assignedCustomer' &&
    field !== 'assignedHelicopterServiceProvider' &&
    field !== 'category'
  ) {
    return <span>{String(value)}</span>;
  }

  if (field === 'assignedCustomer' && resolvedCustomerName) {
    return <span>{resolvedCustomerName}</span>;
  }

  if (field === 'assignedHelicopterServiceProvider' && resolvedHeliProviderName) {
    return <span>{resolvedHeliProviderName}</span>;
  }

  if (field === 'category' && resolvedCategoryName) {
    return <span>{resolvedCategoryName}</span>;
  }

  if (isMetadataField) {
    const [prefix, key] = field.split('.');
    let fieldDef: JsonSchemaType['properties'][string] | undefined;

    if (prefix === 'categoryMetadata') {
      // For categoryMetadata, look in categorySchema
      fieldDef = resolvedCategorySchema?.properties?.[key] as
        | JsonSchemaType['properties'][string]
        | undefined;
    } else {
      // For dynamicMetadata, look in objectSchema first, then categorySchema
      fieldDef =
        (objectSchema?.properties?.[key] as JsonSchemaType['properties'][string]) ||
        (resolvedCategorySchema?.properties?.[key] as JsonSchemaType['properties'][string]);
    }

    if (fieldDef) {
      switch (fieldDef.type) {
        case 'date':
          return typeof value === 'string' && DateTime.fromISO(value).isValid ? (
            <span>{DateTime.fromISO(value).toFormat('dd.MM.yyyy')}</span>
          ) : (
            <span>{String(value)}</span>
          );
        case 'boolean':
          return <span>{t(value ? 'common.yes' : 'common.no')}</span>;
        case 'number':
          return <span>{typeof value === 'number' ? value.toString() : String(value)}</span>;
        case 'contact': {
          const contactValue = value as ContactValue | undefined | null;
          const formatted = formatContactValue(contactValue);
          return formatted ? <span>{formatted}</span> : <span>{t('activity.none')}</span>;
        }
        case 'positions': {
          const positionsValue = value as PositionsValue | undefined | null;
          const formatted = formatPositionsValue(
            positionsValue,
            t('activity.object.positions' as never) as string
          );
          return formatted ? <span>{formatted}</span> : <span>{t('activity.none')}</span>;
        }
        case 'status': {
          const statusValue = value as StatusValue | undefined | null;
          const statusOptions = (fieldDef as { statusOptions?: StatusOption[] }).statusOptions;
          const formatted = formatStatusValue(statusValue, statusOptions, locale, {
            manual: t('activity.object.status.manual' as never) as string,
            scheduled: t('activity.object.status.scheduled' as never) as string,
            ranges: t('activity.object.status.ranges' as never) as string,
            defaultStatus: t('activity.object.status.default' as never) as string,
          });
          return formatted ? <span>{formatted}</span> : <span>{t('activity.none')}</span>;
        }
        default:
          return <span>{String(value)}</span>;
      }
    }
  }

  return <span>{String(value)}</span>;
}
